import { HttpError, useApiUrl, useCustom, useCustomMutation } from '@refinedev/core';
import Response from '@responses/response';
import { getSaSmsErrorCode } from '@utils/sa-sms-error';
import { notification } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Query = Record<string, string | number | boolean | undefined | null>;

const clean = (query?: Query): Record<string, string | number | boolean> => {
    const out: Record<string, string | number | boolean> = {};
    Object.entries(query ?? {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

/** Translated text of an API error: known machine codes live under `errors.*`, anything else is shown as received. */
export const useSaSmsErrorText = () => {
    const { t } = useTranslation(['sa-sms']);
    return useCallback(
        (error: unknown) => {
            const code = getSaSmsErrorCode(error);
            return t(`errors.${code}`, { defaultValue: code });
        },
        [t],
    );
};

/** GET against the super-admin SMS console API (`/v1/sa/sms/*`, root tenant only). */
export const useSaSmsGet = <T>(path: string, query?: Query, enabled = true) => {
    const apiUrl = useApiUrl();
    const result = useCustom<Response<T>, HttpError>({
        url: `${apiUrl}/v1/sa/sms${path}`,
        method: 'get',
        config: { query: clean(query) },
        errorNotification: false,
        queryOptions: { enabled, retry: 1, queryKey: ['sa-sms', path, clean(query)] },
    });
    return {
        data: result.data?.data?.data as T | undefined,
        isLoading: result.isLoading,
        isError: result.isError,
        error: result.error,
        refetch: result.refetch,
    };
};

/** POST / PUT against the console API. Resolves with `data`; an error is shown as a notification AND re-thrown. */
export const useSaSmsWrite = () => {
    const apiUrl = useApiUrl();
    const errorText = useSaSmsErrorText();
    const { mutateAsync, isLoading } = useCustomMutation<Response<unknown>, HttpError>();
    const send = useCallback(
        async <T>(method: 'post' | 'put', path: string, values: unknown): Promise<T> => {
            try {
                const res = await mutateAsync({
                    url: `${apiUrl}/v1/sa/sms${path}`,
                    method,
                    values: values as Record<string, unknown>,
                    errorNotification: false,
                });
                return res.data?.data as T;
            } catch (error) {
                notification.error({ message: errorText(error) });
                throw error;
            }
        },
        [apiUrl, errorText, mutateAsync],
    );
    return useMemo(() => ({ send, isLoading }), [send, isLoading]);
};

/** `sms_messaging_edit` unlocks every write control; without it the console is read-only. */
export const useSaSmsCanEdit = (permissions: string[] | undefined): boolean =>
    !!permissions?.includes('sms_messaging_edit');
